import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { House, FolderOpen, Plus, Mail, Settings, PenLine, ImageIcon, Film, X, Scissors, User, ScanSearch } from "lucide-react";
import NewStoryModal from "@/components/NewStoryModal";
import AstroStudioModal from "@/components/AstroStudioModal";
import ImageGenModal from "@/components/ImageGenModal";
import VideoGenModal from "@/components/VideoGenModal";
import StitchEditor from "@/components/StitchEditor";
import AiOrNotModal from "@/components/AiOrNotModal";

export default function BottomNav() {
  const navigate = useNavigate();
  const location = useLocation();
  const [chooser, setChooser] = useState(false);
  const [modal, setModal] = useState(null); // story | image | video

  const active = (p) => location.pathname === p;
  const openModal = (m) => { setChooser(false); setModal(m); };

  const Item = ({ icon: Icon, label, onClick, isActive, testid }) => (
    <button onClick={onClick} data-testid={testid} className="flex flex-col items-center gap-1 flex-1 py-1">
      <Icon size={22} className={isActive ? "text-gold" : "text-zinc-500"} />
      <span className={`text-[10px] ${isActive ? "text-gold" : "text-zinc-600"}`}>{label}</span>
    </button>
  );

  const chooserItems = [
    { m: "aiornot", icon: ScanSearch, label: "AI or Not", desc: "Check if an image or video is AI-generated" },
    { m: "astro", icon: PenLine, label: "Astro Studio", desc: "Story → Panels → Animation" },
    { m: "image", icon: ImageIcon, label: "Image", desc: "Generate images with GPT" },
    { m: "video", icon: Film, label: "Animation", desc: "Animate with Seedance 2.5" },
    { m: "stitch", icon: Scissors, label: "Stitch ✂️", desc: "Trim, text, crop, capture & sound" },
  ];

  return (
    <>
      <nav className="fixed bottom-0 left-0 right-0 z-40 bg-ink-950/90 backdrop-blur-xl border-t border-white/10 px-4 pb-2 pt-1.5">
        <div className="max-w-lg mx-auto flex items-center justify-between">
          <Item icon={House} label="Home" isActive={active("/home")} onClick={() => navigate("/home")} testid="nav-home" />
          <Item icon={User} label="You" isActive={location.pathname.startsWith("/u/") || location.pathname === "/profile"} onClick={() => navigate("/profile")} testid="nav-you" />
          <button onClick={() => setChooser(true)} data-testid="nav-create" className="w-12 h-12 -mt-3 rounded-full bg-gold text-ink-950 flex items-center justify-center shadow-[0_0_20px_rgba(198,138,91,0.5)] hover:bg-goldlight transition-colors">
            <Plus size={26} />
          </button>
          <Item icon={Mail} label="Notifications" isActive={active("/notifications")} onClick={() => navigate("/notifications")} testid="nav-notifications" />
          <Item icon={Settings} label="Settings" isActive={active("/settings")} onClick={() => navigate("/settings")} testid="nav-settings" />
        </div>
      </nav>

      {chooser && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center" data-testid="create-chooser">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setChooser(false)} />
          <div className="relative w-full sm:max-w-md bg-ink-900 border-t sm:border border-zinc-800 sm:rounded-2xl p-5 pb-8 space-y-3">
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-heading font-black uppercase tracking-tight text-xl">Create</h3>
              <button onClick={() => setChooser(false)} className="text-zinc-500 hover:text-white"><X size={22} /></button>
            </div>
            {chooserItems.map((c) => (
              <button key={c.m} onClick={() => !c.soon && openModal(c.m)} data-testid={`create-${c.m}`}
                disabled={c.soon} aria-disabled={c.soon}
                className={`w-full flex items-center gap-4 border rounded-xl p-4 text-left transition-colors ${c.soon ? "border-zinc-800/70 opacity-55 cursor-not-allowed" : "border-zinc-800 hover:border-gold hover:bg-[#1a140d]"}`}>
                <div className={`w-11 h-11 rounded-lg bg-[#1a140d] border flex items-center justify-center ${c.soon ? "border-zinc-700" : "border-gold/30"}`}><c.icon size={22} className={c.soon ? "text-zinc-500" : "text-gold"} /></div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <div className="font-heading font-bold">{c.label}</div>
                    {c.soon && <span className="text-[9px] font-heading font-bold uppercase tracking-wide rounded-full px-2 py-0.5 border border-gold/40 text-gold bg-[#1a140d]" data-testid={`create-${c.m}-soon`}>Coming Soon</span>}
                  </div>
                  <div className="text-xs text-zinc-500 truncate">{c.soon ? "In development — available soon" : c.desc}</div>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      <NewStoryModal open={modal === "story"} onOpenChange={(o) => !o && setModal(null)} />
      <AiOrNotModal open={modal === "aiornot"} onOpenChange={(o) => !o && setModal(null)} />
      <AstroStudioModal open={modal === "astro"} onOpenChange={(o) => !o && setModal(null)} />
      <ImageGenModal open={modal === "image"} onOpenChange={(o) => !o && setModal(null)} onDone={() => navigate("/profile?tab=folder")} />
      <VideoGenModal open={modal === "video"} onOpenChange={(o) => !o && setModal(null)} onDone={() => navigate("/profile?tab=folder")} />
      <StitchEditor open={modal === "stitch"} onOpenChange={(o) => !o && setModal(null)} onExported={() => navigate("/profile?tab=folder")} />
    </>
  );
}
